import { useSubscription } from "@apollo/client/react";
import { useMemo } from "react";

import {
  applyObjectChangesToCache,
  type ObjectChange,
} from "@/app/_/subscriptions/writeObjectChangeToCache";
import { Subscriptions } from "@/constants";
import { State } from "@/state";

/**
 * Subscribes to `objectsChanged` for every entity the user currently has
 * in scope. The backend publishes a batched event per service-layer
 * transaction; we materialise each non-deleted ``node`` payload into the
 * normalised cache (so list/connection views pick up field-level updates
 * like ``ExecutionObject.status``) and evict on ``deleted``.
 *
 * One hook call per entity keeps `useSubscription` stable — Apollo
 * multiplexes them over the single shared `graphql-ws` connection.
 */
function EntitySubscriber({ entityIdentifier }: { entityIdentifier: string }) {
  useSubscription(Subscriptions.OBJECTS_CHANGED, {
    variables: { entityIdentifier },
    onData: ({ client, data }) => {
      const event = (data.data as any)?.objectsChanged;
      if (!event?.changes?.length) return;

      applyObjectChangesToCache(client, event.changes as ObjectChange[]);
    },
  });

  return null;
}

export function ObjectsChangedSubscriber() {
  // Fan out one subscription per active entity. Mutations can target any
  // entity the user has active — e.g. a workflow run publishes change
  // events on the workflow's owning entity, which is not necessarily
  // the currently scoped one. ``scopedEntityIdentifier`` is a *display*
  // filter; if we narrow the subscription set to only the scoped entity
  // we'd silently drop ``objectsChanged`` events for other-entity work
  // (e.g. workflow threads updating ``queued -> running -> succeeded``).
  //
  // The scoped entity is unioned in defensively so we never miss it
  // when ``activeEntityIdentifiers`` hasn't loaded yet. Subscribe on
  // every entity the user can access — not just the active subset —
  // so cross-entity panels (e.g. a workflow owned by entity A while
  // the UI scope is narrowed to entity B) still receive lifecycle
  // events on the workflow's owning entity channel.
  //
  // ``graphql-ws`` multiplexes all of these over the single shared socket.
  const entities = State((state) => state.entities);
  const scoped = State((state) => state.scopedEntityIdentifier);
  const active = State((state) => state.activeEntityIdentifiers);
  const entityIds = useMemo(() => {
    const accessibleEntityIds = (entities ?? [])
      .map((entity) => entity.identifier)
      .filter(Boolean);
    return Array.from(
      new Set([
        ...accessibleEntityIds,
        ...(active ?? []),
        ...(scoped ? [scoped] : []),
      ]),
    );
  }, [entities, active, scoped]);

  if (!entityIds.length) return null;

  return (
    <>
      {entityIds.map((id) => (
        <EntitySubscriber key={id} entityIdentifier={id} />
      ))}
    </>
  );
}
